import { ref, watchEffect } from 'vue'
import { debounce, throttle, get } from 'lodash'

/**
 * @description 请求封装
 * @param  { Promise } promise 请求接口的promise对象
 * @param  { Object } options 其他配置参数
 * @param  { Boolean } options.autoRun 默认是否自动请求
 * @param  { Boolean } options.params 请求参数
 * @param  { Number } options.debounce 防抖时间
 * @param  { Number } options.throttle 节流时间
 * @param  { Function } options.onFinish 接口响应成功后的回调
 * @param  { Function } options.onError 接口响应失败后的回调
 * @returns { Object } res 接口响应内容
 * @returns { Number } resCode 接口响应内容
 * @returns { Boolean } loading 是否在加载中
 * @returns { Object } error 接口响应失败内容
 * @returns { Function } getData 接口响应失败内容
 */
const useFetch = (promise = new Promise(), options = {}) => {
	if (!promise) {
    throw new Error('必须传入一个promise对象')
  }
  const loading = ref(false)
	const res = ref(null)
	const error = ref(null)
	let autoRun = ref(true) // 默认发起请求

	let resCode = ref(0)

	const getData = async newParams => {
		loading.value = true
		try {
			// 默认参数，和传入参数合并
			const baseParams = options?.params ?? {}
			const params = {
				...baseParams,
				...newParams
			}
			const responseData = await promise(params)
			res.value = get(responseData, 'data', {})
			resCode.value = get(responseData, 'code', 0)
			options.onFinish && options.onFinish(res.value)
		} catch (err) {
			error.value = err
			console.error(err)
			options.onError?.(err)
		} finally {
			loading.value = false
		}
	}

	// 防抖
	if (options?.debounce) {
		const debounceGetData = debounce(getData, options.debounce)
		debounceGetData()
	}

	// 节流
	if (options?.throttle) {
		const throttleGetData = throttle(getData, options.throttle)
		throttleGetData()
	}

	// 自动查询
	watchEffect(() => {
		autoRun.value = options?.autoRun ?? true
		if (autoRun.value && !res.value) {
      getData()
    }
	})

	return {
		loading,
		res,
		resCode,
		error,
		autoRun,
		getData
	}
}

export default useFetch
